<template>
  <div class="qaqc-dl-aux-distribution">
    <div class="table-content">
      <div class="table-wrap">
        <div class="table-title-div">
          <p class="table-title">{{ sectionTitle }}</p>
          <div class="qaqc-scorecard-header-actions">
            <div class="csv-btn" @click="downloadTableCSV">
              <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
            </div>
            <v-btn
              icon
              variant="text"
              size="small"
              class="qaqc-scorecard-collapse-btn"
              :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
              @click="isCollapsed = !isCollapsed"
            >
              <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div
      class="qaqc-overview-distribution qaqc-initial-screening-plots-layout"
      :class="{ 'qaqc-dl-aux-distribution-fullwidth': !hasPlotList }"
      v-show="!isCollapsed"
    >
      <div v-if="hasPlotList" class="qaqc-initial-screening-plot-selector qaqc-overview-plot-selector">
        <h6 class="qaqc-plot-selector-title">Distributions</h6>
        <button
          v-for="opt in plotOptions"
          :key="opt.id"
          type="button"
          :class="['qaqc-initial-screening-plot-item', { active: selectedPlotId === opt.id }]"
          @click="$emit('update:selectedPlotId', opt.id)"
        >
          {{ opt.label }}
        </button>
      </div>
      <div
        class="qaqc-initial-screening-plot-panel qaqc-overview-plot-panel"
        :class="{ 'qaqc-dl-aux-plot-panel-solo': !hasPlotList }"
      >
        <div>
          <div class="qaqc-test-chart-name-top">
            <h6>{{ chartHeading }}</h6>
            <div
              v-if="isHistogram"
              class="qaqc-distribution-bins-block qaqc-distribution-modal-tab-div"
            >
              <div class="qaqc-distribution-tab-div">
                <p class="qaqc-distribution-tab-label">Number of bins:</p>
                <div class="qaqc-distribution-range-value">{{ numBins }}</div>
                <input
                  type="range"
                  class="slider qaqc-distribution-range-input"
                  min="2"
                  max="100"
                  step="1"
                  :value="numBins"
                  @input="onBinsRangeInput"
                />
              </div>
            </div>
          </div>
          <div class="qaqc-distribution-chart-inner">
            <div class="qaqc-distribution-chart-stack">
              <div ref="chartHostRef" class="qaqc-distribution-bar-chart qaqc-distribution-plotly-host"></div>
              <div
                v-show="showNoData"
                class="qaqc-distribution-no-data tabs-no-test-div qaqc-distribution-no-data-overlay"
              >
                <h4 class="qaqc-distribution-no-data-title">Data not available</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import { drawQaqcDistributionPlotly } from "./distributionPlotlyDraw.js";

export default {
  name: "DlAuxDistributionSection",
  props: {
    sectionTitle: { type: String, required: true },
    plotOptions: { type: Array, default: () => [] },
    selectedPlotId: { type: String, default: "" },
    distributionData: {
      type: Object,
      required: true,
    },
    csvFileName: { type: String, default: "distribution_chart.csv" },
  },
  emits: ["update:selectedPlotId"],
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
      numBins: 10,
    };
  },
  computed: {
    hasPlotList() {
      return Array.isArray(this.plotOptions) && this.plotOptions.length > 0;
    },
    plotKind() {
      return (this.distributionData && this.distributionData.plotKind) || "empty";
    },
    isHistogram() {
      return this.plotKind === "histogram";
    },
    isCategoricalBar() {
      return this.plotKind === "categoricalBar";
    },
    showNoData() {
      return this.plotKind === "unavailable" || this.plotKind === "empty";
    },
    selectedPlotLabel() {
      const opt = this.plotOptions.find((o) => o.id === this.selectedPlotId);
      return (opt && opt.label) || "";
    },
    chartHeading() {
      if (this.hasPlotList) {
        return this.selectedPlotLabel || this.sectionTitle;
      }
      return this.sectionTitle;
    },
  },
  watch: {
    selectedPlotId() {
      this.numBins = 10;
    },
    distributionData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderChart();
          this.schedulePlotlyResize();
        });
      },
    },
    numBins() {
      if (this.isHistogram) this.$nextTick(() => this.renderDistributionPlot());
    },
    isCollapsed(v) {
      if (!v && (this.isHistogram || this.isCategoricalBar)) {
        this.$nextTick(() => this.schedulePlotlyResize());
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
      this.schedulePlotlyResize();
    });
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
    this.purgePlotly();
  },
  methods: {
    onBinsRangeInput(e) {
      const n = Number(e.target.value);
      if (Number.isFinite(n)) this.numBins = n;
    },
    schedulePlotlyResize() {
      requestAnimationFrame(() => {
        this.onWindowResize();
        requestAnimationFrame(() => {
          this.onWindowResize();
        });
      });
    },
    onWindowResize() {
      const el = this.$refs.chartHostRef;
      if (!el || this.showNoData || (!this.isHistogram && !this.isCategoricalBar)) return;
      try {
        Plotly.Plots.resize(el);
      } catch {
        /* plot may not be initialized */
      }
    },
    purgePlotly() {
      const el = this.$refs.chartHostRef;
      if (!el) return;
      try {
        Plotly.purge(el);
      } catch {
        /* container may be empty */
      }
    },
    renderChart() {
      this.purgePlotly();
      const el = this.$refs.chartHostRef;
      if (this.showNoData) {
        if (el) el.innerHTML = "";
        return;
      }
      if (!el) return;
      el.innerHTML = "";
      if (this.isHistogram || this.isCategoricalBar) this.renderDistributionPlot();
    },
    renderDistributionPlot() {
      const el = this.$refs.chartHostRef;
      if (!el) return;
      const drawn = drawQaqcDistributionPlotly(el, this.distributionData, this.numBins);
      if (drawn && typeof drawn.then === "function") {
        drawn.then(() => this.schedulePlotlyResize());
      } else {
        this.schedulePlotlyResize();
      }
    },
    downloadTableCSV() {
      let csvContent = "";
      if (this.isHistogram) {
        const vals = (this.distributionData?.rawValues) || [];
        if (vals.length === 0) return;
        csvContent = ["Value", ...vals.map((v) => String(v))].join("\n");
      } else if (this.isCategoricalBar) {
        const cats = this.distributionData?.categories || [];
        const counts = this.distributionData?.categoryCounts || [];
        if (cats.length === 0) return;
        csvContent = [
          ["Category", "Count"].join(","),
          ...cats.map((c, i) => [String(c ?? ""), String(counts[i] ?? "")].join(",")),
        ].join("\n");
      } else {
        return;
      }
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", this.csvFileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped>
.qaqc-dl-aux-distribution-fullwidth {
  flex-wrap: nowrap;
}

.qaqc-dl-aux-plot-panel-solo {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.qaqc-dl-aux-distribution :deep(.qaqc-distribution-tab-label),
.qaqc-dl-aux-distribution :deep(.qaqc-distribution-range-value) {
  color: #ffffff;
}

.qaqc-distribution-bins-block {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.qaqc-distribution-modal-tab-div {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qaqc-distribution-tab-div {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: min(320px, 100%);
}

.qaqc-distribution-tab-label {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.qaqc-distribution-range-value {
  min-width: 2ch;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
}

.qaqc-distribution-range-input {
  flex: 1;
  min-width: 120px;
  vertical-align: middle;
}

.qaqc-distribution-chart-inner {
  min-height: 280px;
  padding: 8px 0;
}

.qaqc-distribution-chart-stack {
  position: relative;
  width: 100%;
  min-height: 360px;
}

.qaqc-distribution-plotly-host {
  width: 100%;
  height: 360px;
  min-height: 360px;
  box-sizing: border-box;
}

.qaqc-distribution-no-data-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  min-height: 360px;
}
</style>
